import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import confetti from 'canvas-confetti';
import { trackEvent } from '@/lib/analytics';
import { useWaitlistCount } from '@/hooks/useWaitlistCount';
import ScarcityBar from './ScarcityBar';
import WelcomeConfirmation from './WelcomeConfirmation';

const MAX_FOUNDING = 10_000;

interface WaitlistFormProps {
  id?: string;
  referredBy?: string | null;
  onSignup?: () => void;
}

const generateCode = () => {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  return Array.from({ length: 8 }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
};

const WaitlistForm = ({ id, referredBy, onSignup }: WaitlistFormProps) => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<{ status: string; explorer_number: number; referral_code: string } | null>(null);
  const [error, setError] = useState<'invalid' | 'network' | null>(null);
  const [displayNumber, setDisplayNumber] = useState(0);
  const [overflowJoined, setOverflowJoined] = useState(false);
  const { count, refetch: refetchCount } = useWaitlistCount();
  const isFull = count >= MAX_FOUNDING;

  // Animate explorer number count-up
  useEffect(() => {
    if (!result) return;
    if (result.status === 'existing') {
      setDisplayNumber(result.explorer_number);
      return;
    }
    const target = result.explorer_number;
    const start = Math.max(1, target - 40);
    let current = start;
    const interval = setInterval(() => {
      current += 1;
      setDisplayNumber(current);
      if (current >= target) clearInterval(interval);
    }, 30);
    return () => clearInterval(interval);
  }, [result]);

  const handleOverflowSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const trimmed = email.trim().toLowerCase();
    if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setError('invalid');
      return;
    }
    setLoading(true);
    try {
      const code = generateCode();
      await supabase.from('general_waitlist').insert({ email: trimmed, referral_code: code, referred_by: referredBy ?? null });
      setOverflowJoined(true);
      onSignup?.();
      await refetchCount();
      trackEvent('general_waitlist_submitted');
    } catch {
      setError('network');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const trimmed = email.trim().toLowerCase();
    if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setError('invalid');
      return;
    }

    setLoading(true);
    try {
      // Use the server-side RPC which handles check + insert atomically
      const { data, error: rpcError } = await supabase.rpc('waitlist_signup', {
        p_email: trimmed,
        p_referred_by: referredBy ?? undefined,
      });

      if (rpcError) throw rpcError;

      const parsed = data as { status: string; explorer_number: number; referral_code: string };
      setResult(parsed);

      if (parsed.status === 'new') {
        onSignup?.();
        await refetchCount();
        trackEvent('waitlist_submitted');
        confetti({
          particleCount: 100,
          spread: 70,
          origin: { y: 0.6 },
          colors: ['#6d28d9', '#8b5cf6', '#ea580c', '#f97316', '#a78bfa'],
        });
      } else {
        onSignup?.();
      }
    } catch {
      setError('network');
      trackEvent('waitlist_error', { type: 'network' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id={id} className="relative py-16 px-6">
      <div className="max-w-xl mx-auto">
        {/* Full state — general waitlist */}
        {isFull && !result ? (
          <div className="text-center">
            <p className="font-bebas text-3xl text-foreground mb-2">Founding spots are full 🌍</p>
            <p className="font-mono text-sm text-muted-foreground mb-6">
              Founding spots are full. You're on the general waitlist. We'll notify you at launch.
            </p>
            {!overflowJoined ? (
              <form onSubmit={handleOverflowSubmit} className="flex flex-col sm:flex-row gap-3">
                <input
                  type="email"
                  value={email}
                  onChange={e => { setEmail(e.target.value); setError(null); }}
              placeholder="your@email.com"
              inputMode="email"
              className={`flex-1 h-12 px-4 rounded bg-card border ${
                error === 'invalid' ? 'border-dv-orange' : 'border-primary/40'
              } text-foreground font-mono placeholder:text-muted-foreground focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-colors`}
              aria-label="Email address"
            />
            <button
              type="submit"
              disabled={loading}
              className="h-12 px-8 rounded bg-card border border-border text-foreground font-bebas text-lg tracking-wide hover:border-primary/40 disabled:opacity-50 transition-all whitespace-nowrap"
            >
              {loading ? '...' : 'Join General Waitlist'}
                </button>
              </form>
            ) : (
              <p className="font-bebas text-xl text-foreground">You're on the general waitlist ✓</p>
            )}
          </div>
        ) : !result ? (
          <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3">
            <input
              type="email"
              value={email}
              onChange={e => { setEmail(e.target.value); setError(null); }}
              onFocus={() => trackEvent('waitlist_form_focused')}
              placeholder="your@email.com"
              inputMode="email"
              className={`flex-1 h-12 px-4 rounded bg-card border ${
                error === 'invalid' ? 'border-dv-orange' : 'border-primary/40'
              } text-foreground font-mono placeholder:text-muted-foreground focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-colors`}
              aria-label="Email address"
            />
            <button
              type="submit"
              disabled={loading}
              className="h-12 px-8 rounded bg-primary text-primary-foreground font-bebas text-lg tracking-wide hover:bg-primary/90 disabled:opacity-50 transition-all whitespace-nowrap"
            >
              {loading ? '...' : 'Launch Me In 🚀'}
            </button>
          </form>
        ) : (
          <WelcomeConfirmation
            explorerNumber={result.explorer_number}
            referralCode={result.referral_code}
            displayNumber={displayNumber}
            isExisting={result.status === 'existing'}
          />
        )}

        {error === 'invalid' && (
          <p className="mt-2 text-sm font-mono" style={{ color: 'hsl(var(--orange))' }}>That doesn't look right.</p>
        )}
        {error === 'network' && (
          <div className="mt-2 text-sm font-mono text-muted-foreground">
            Something glitched. GLITCH is probably responsible.{' '}
            <button onClick={() => setError(null)} className="text-primary hover:underline">Try again?</button>
          </div>
        )}

        {/* Scarcity bar — only show before signup */}
        {!result && <ScarcityBar />}
      </div>
    </section>
  );
};

export default WaitlistForm;
